import React, { useState, useEffect, useCallback } from 'react';
import InteractiveMap from './components/InteractiveMap';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import MainTab from './components/MainTab';
import PropertiesTab from './components/PropertiesTab';
import AboutTab from './components/AboutTab';
import ContactTab from './components/ContactTab';
import AdminTab from './components/AdminTab';
import './App.css';
import { I18nProvider } from './i18n';

const ALL_TABS = ['who', 'spaces', 'map', 'contact', 'admin'];

const getTabFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  return ALL_TABS.includes(hash) ? hash : 'who';
};

const App = ({ role = 'guest' }) => {
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [activeTab, setActiveTab] = useState(getTabFromHash);
  const [lang, setLang] = useState('es');

  // Listen for browser back / forward
  useEffect(() => {
    // Set initial hash if not present
    if (!window.location.hash) {
      window.history.replaceState(null, '', '#who');
    }
    const onPopState = () => {
      setActiveTab(getTabFromHash());
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const toggleLang = (next) => {
    if (next) {
      setLang(next);
    } else {
      setLang((prev) => (prev === 'en' ? 'es' : 'en'));
    }
  };

  const landParcels = [
    {
      id: 'cluster-1',
      name: 'Colinas Eucalipto',
      image: '/Images/properties/colinas-eucalipto.png',
      status: 'available',
      price: '2,500 MXN per sqm',
      size: '181,400 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Adjacent to lake and green areas, with privileged position close to main access while preserving privacy.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 1',
      coordinates: [
        [20.803004548, -101.307452473],
        [20.80321349, -101.307889686],
        [20.805924248, -101.30765316],
        [20.805927418, -101.307356324],
        [20.809060249, -101.307102411],
        [20.808985963, -101.304116182],
        [20.806943684, -101.304330466],
        [20.806928071, -101.304646931],
        [20.806506513, -101.30470045],
        [20.806103867, -101.304961863],
        [20.804994525, -101.305018013],
        [20.80466888, -101.305110033],
        [20.804046377, -101.305112406],
        [20.803556915, -101.305338206],
        [20.803542478, -101.305571652],
        [20.803239818, -101.305788565],
        [20.803292412, -101.306672172],
        [20.803004548, -101.307452473]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Commercial & Services, or Residential use',
        'Privileged position close to main access, adjacent to green areas and lake'
      ]
    },
    {
      id: 'cluster-2',
      name: 'Lomas Pitahaya',
      image: '/Images/properties/lomas-pitahaya.png',
      status: 'available',
      price: '1,250 MXN per sqm',
      size: '76,600 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Elevated site with beautiful views and exceptional privacy, adjacent to the lake and large green area.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 2',
      coordinates: [
        [20.801278416, -101.305011716],
        [20.800982971, -101.305245178],
        [20.800684529, -101.305294163],
        [20.800367346, -101.305251533],
        [20.799886338, -101.305114709],
        [20.799768931, -101.30456802],
        [20.799436892, -101.304573491],
        [20.799102665, -101.300406132],
        [20.802659274, -101.300089374],
        [20.802841892, -101.300482235],
        [20.801856944, -101.301069046],
        [20.801111099, -101.301721948],
        [20.800571894, -101.302452526],
        [20.800327299, -101.303364226],
        [20.800631617, -101.304244141],
        [20.800881806, -101.304649824],
        [20.801278416, -101.305011716]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Commercial & Services, or Residential use',
        'Elevated site with beautiful views'
      ]
    },
    {
      id: 'cluster-3',
      name: 'Valle Cedrillo',
      image: '/Images/properties/valle-cedrillo.png',
      status: 'available',
      price: '1,250 MXN per sqm',
      size: '351,800 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Vast green surroundings with panoramic sunset views.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 3',
      coordinates: [
        [20.801215991, -101.303200031],
        [20.801461968, -101.303714514],
        [20.801047573, -101.304054467],
        [20.80175117, -101.304724745],
        [20.802453701, -101.305065632],
        [20.802834683, -101.305333497],
        [20.803792964, -101.304426837],
        [20.803783588, -101.30403016],
        [20.804846699, -101.303111773],
        [20.805504688, -101.302902877],
        [20.805634964, -101.303037974],
        [20.805262482, -101.303561493],
        [20.805563141, -101.304308526],
        [20.805987843, -101.304307648],
        [20.806177502, -101.304120416],
        [20.806557356, -101.304070953],
        [20.806814726, -101.303692583],
        [20.806111911, -101.303430074],
        [20.808676937, -101.303304778],
        [20.809262146, -101.302715792],
        [20.81043642, -101.299934434],
        [20.8102295, -101.296750832],
        [20.810646962, -101.296667966],
        [20.810414364, -101.295937269],
        [20.808270627, -101.2962271],
        [20.80857928, -101.296859773],
        [20.808830108, -101.297061751],
        [20.808819329, -101.297769197],
        [20.808541791, -101.297860677],
        [20.808546714, -101.298779629],
        [20.806393326, -101.299054075],
        [20.806352445, -101.299663505],
        [20.804839486, -101.299946724],
        [20.804524318, -101.300687021],
        [20.804768594, -101.301698337],
        [20.805326379, -101.302606971],
        [20.804836386, -101.302839159],
        [20.804755255, -101.302561844],
        [20.804173844, -101.302871682],
        [20.801215991, -101.303200031]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Commercial & Services, or Residential use',
        'Panoramic sunset views'
      ]
    },
    {
      id: 'cluster-4',
      name: 'Prado Sabina',
      image: '/Images/properties/prado-sabina.png',
      status: 'available',
      price: '750 MXN per sqm',
      size: '408,600 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Flat land with excellent topography and gentle contours, with extensive frontage along the boulevard and strong potential for commercial use, adjacent to green area.',
      encumbrances: 'None',
      utilities: 'No',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 4',
      coordinates: [
        [20.811187121, -101.294966538],
        [20.810855548, -101.291978414],
        [20.807859052, -101.291969032],
        [20.80773696, -101.288239798],
        [20.807402979, -101.288414434],
        [20.80622998, -101.288518207],
        [20.805388174, -101.289572633],
        [20.804952064, -101.290357207],
        [20.804764488, -101.290935692],
        [20.80459637, -101.292649238],
        [20.805046443, -101.292949685],
        [20.805076989, -101.293814977],
        [20.804701767, -101.29479967],
        [20.804461118, -101.294896941],
        [20.804337558, -101.297844217],
        [20.804434611, -101.298376961],
        [20.806882181, -101.298183007],
        [20.807272992, -101.29770237],
        [20.807301237, -101.297382121],
        [20.807422323, -101.297139264],
        [20.807715536, -101.297124281],
        [20.807832445, -101.295707839],
        [20.807928273, -101.295246448],
        [20.811064203, -101.294982072],
        [20.811187121, -101.294966538] // closed polygon
      ],
      color: '#C9A227',
      features: [
        'Utility access: No',
        'Approved for: Commercial & Services, or Residential use',
        'Excellent topography with boulevard frontage'
      ]
    },
    {
      id: 'cluster-5',
      name: 'Mangle Escondido',
      image: '/Images/properties/mangle-escondido.png',
      status: 'available',
      price: '750 MXN per sqm',
      size: '294,500 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Adjacent to green area with beautiful panoramic views.',
      encumbrances: 'None',
      utilities: 'No',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 5',
      coordinates: [
        [20.798901608, -101.298088867],
        [20.802537436, -101.297824977],
        [20.802472458, -101.297073455],
        [20.803843585, -101.296924958],
        [20.803960439, -101.294849724],
        [20.803750732, -101.294838997],
        [20.803564644, -101.293400433],
        [20.803908624, -101.292319722],
        [20.804242136, -101.292464284],
        [20.804351426, -101.290460977],
        [20.805303213, -101.28877702],
        [20.806270151, -101.288022223],
        [20.807338691, -101.287893504],
        [20.807079761, -101.287128758],
        [20.8065965, -101.287276989],
        [20.805485412, -101.288053507],
        [20.804795182, -101.288697427],
        [20.804499424, -101.289583353],
        [20.803612979, -101.290668609],
        [20.802793558, -101.290465558],
        [20.801980522, -101.291032498],
        [20.801407561, -101.290947506],
        [20.801001676, -101.291320586],
        [20.800707955, -101.292017829],
        [20.801084696, -101.292160921],
        [20.801657828, -101.291668631],
        [20.80178933, -101.292004263],
        [20.801690074, -101.293042703],
        [20.800965805, -101.294071313],
        [20.800212411, -101.295523254],
        [20.79893677, -101.29742789],
        [20.798900409, -101.297933671],
        [20.798901608, -101.298088867] // closed polygon
      ],
      color: '#C9A227',
      features: [
        'Utility access: No',
        'Approved for: Commercial & Services, or Residential use',
        'Beautiful panoramic views'
      ]
    },
    {
      id: 'cluster-6',
      name: 'Huerta Granado',
      image: '/Images/properties/huerta-granado.png',
      status: 'available',
      price: '550 MXN per sqm',
      size: '283,100 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Private cluster with panoramic views and adjoining green areas. Offers incredible vistas and scenic rocky outcrops.',
      encumbrances: 'None',
      utilities: 'No',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: 'Cluster 6',
      coordinates: [
        [20.79866044579943, -101.2942246605035],
        [20.79862424119008, -101.2928809912637],
        [20.79867287228153, -101.2920160403169],
        [20.79881049614025, -101.2910873188586],
        [20.79927895824935, -101.2906780967161],
        [20.79928280657137, -101.2896177288775],
        [20.79888394637157, -101.2894122249389],
        [20.79876070371632, -101.2891485738417],
        [20.79891508842923, -101.2886110605679],
        [20.79987936273154, -101.2886896666968],
        [20.80032016178922, -101.2885207819463],
        [20.80088115672516, -101.2885197644769],
        [20.80172123161651, -101.2882677797992],
        [20.8024502559307, -101.2882898617247],
        [20.80293790513853, -101.2881957358133],
        [20.80344211962397, -101.287762897941],
        [20.80397663343476, -101.2872987678748],
        [20.80453936010121, -101.2868075054175],
        [20.80564978397099, -101.286499707364],
        [20.80665450623158, -101.2862914757618],
        [20.80689187262386, -101.2865056092491],
        [20.80662885156592, -101.2869115020765],
        [20.80617884910264, -101.2876454245679],
        [20.80518789835455, -101.288374513643],
        [20.80447805606096, -101.2892641069783],
        [20.80412478657878, -101.2900139966823],
        [20.80359801847614, -101.2905335368188],
        [20.80274299358434, -101.2904984246007],
        [20.80187253054301, -101.290877454505],
        [20.80124468383869, -101.290853572766],
        [20.80079515664788, -101.2908666150038],
        [20.80030847850966, -101.2912687955785],
        [20.80007995072206, -101.2922220122818],
        [20.80027793626304, -101.2925762242651],
        [20.80051777716377, -101.2927182833441],
        [20.80093581345419, -101.2927292034994],
        [20.80118279981033, -101.2926998916374],
        [20.80139444952243, -101.2924605985355],
        [20.80130527659093, -101.2931974307988],
        [20.80110209028218, -101.293486746829],
        [20.80085717576165, -101.2938100489632],
        [20.80052035780021, -101.294214255443],
        [20.7999392408836, -101.2952232543739],
        [20.79924574938329, -101.2960345664529],
        [20.79902577323393, -101.2970237201274],
        [20.79866044579943, -101.2942246605035]
      ],
      color: '#C9A227',
      features: [
        'Utility access: No',
        'Approved for: Commercial & Services, or Residential use',
        'Panoramic views with scenic rocky outcrops'
      ]
    },
    {
      id: 'parcel-6',
      name: 'Campos Lavanda',
      image: '/Images/properties/colinas-eucalipto.png',
      status: 'sold',
      price: 'n/a',
      size: '200,000 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Residential area',
      encumbrances: 'None',
      coordinates: [
        [20.802838718, -101.307509082],
        [20.803046274, -101.307522099],
        [20.803211828, -101.307887644],
        [20.805928833, -101.307671204],
        [20.805943879, -101.307372429],
        [20.809051056, -101.307126531],
        [20.809139763, -101.309854253],
        [20.808770781, -101.309945083],
        [20.808687373, -101.310297582],
        [20.806599629, -101.310444179],
        [20.806505807, -101.310746985],
        [20.803972529, -101.310997242],
        [20.803491417, -101.309186226],
        [20.80285949, -101.307933599],
        [20.802838718, -101.307509082]
      ],
      color: '#C9A227',
      features: [
        'SOLD',
      ]
    }
    ,
    {
      id: 'cluster-271',
      name: 'Oasis Nogal',
      image: '/Images/properties/olivo-azul-oasis-nogal.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '54,300 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Dual access via Aldama neighborhood road and main boulevard. Prepared for vertical construction with a solid hydraulic base of tepetate soil.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: '271',
      coordinates: [
        [20.8013331726234, -101.3167256809683],
        [20.8011320680291, -101.3144502324635],
        [20.80251442781527, -101.3144976812023],
        [20.80252391938551, -101.3136555264797],
        [20.80309870893648, -101.3136870907611],
        [20.80309898119801, -101.3154438507069],
        [20.8029889029255, -101.3165316318416],
        [20.8013331726234, -101.3167256809683]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Dual access via Aldama road and main boulevard'
      ]
    },
    {
      id: 'area-a1',
      name: 'Encino Dorado',
      image: '/Images/properties/encino-dorado-pradera-clavel.png',
      status: 'available',
      price: '3,500 MXN per sqm',
      size: '93,300 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime commercial zone potential with high-impact visibility, directly adjacent to the highway.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zone 1 up',
      coordinates: [
        [20.80468937873676, -101.3225155419927],
        [20.80404834953942, -101.3214287752877],
        [20.80395154461872, -101.3209758166573],
        [20.80478544539602, -101.3206424128627],
        [20.80517715745797, -101.3203276082626],
        [20.80598965045768, -101.3200289439071],
        [20.80614243081233, -101.319919668778],
        [20.80633242739277, -101.3197005517792],
        [20.80649472184411, -101.3196352959722],
        [20.80673475935443, -101.3195893139038],
        [20.80733768207477, -101.3196944027296],
        [20.80722736543398, -101.3203859125606],
        [20.80700644493996, -101.3211214992278],
        [20.80744408597604, -101.3214466732698],
        [20.80742053433495, -101.3222496617677],
        [20.806621644129, -101.3223953295261],
        [20.80667695614119, -101.3228155749452],
        [20.80548373697903, -101.3230864284855],
        [20.80483140150183, -101.3229711529766],
        [20.80468937873676, -101.3225155419927]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Prime commercial zone with highway visibility'
      ]
    },
    {
      id: 'area-a2',
      name: 'Pradera Clavel',
      image: '/Images/properties/encino-dorado-pradera-clavel.png',
      status: 'available',
      price: '3,500 MXN per sqm',
      size: '16,200 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime commercial zone potential with high-impact visibility, directly adjacent to highway.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zone 1 down',
      coordinates: [
        [20.80353364580352, -101.3219966664815],
        [20.80324319012013, -101.322081341385],
        [20.80307644996673, -101.3213443949859],
        [20.80368502488185, -101.3210809104095],
        [20.80380746213498, -101.3215977596367],
        [20.80408458127903, -101.3220121719985],
        [20.80433477700658, -101.3223881273165],
        [20.80454049450756, -101.3229630744136],
        [20.80432419869725, -101.3232211002734],
        [20.80415999767414, -101.3233120448124],
        [20.80387821109331, -101.3233934631741],
        [20.80353364580352, -101.3219966664815]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Prime commercial zone with highway visibility'
      ]
    },
    {
      id: 'area-vertical-1',
      name: 'Altos Laurel',
      image: '/Images/properties/altos-laurel-cerro-datil.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '9,700 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zone 2 up',
      coordinates: [
        [20.80451386620924, -101.3173144171249],
        [20.80431144753429, -101.317499874038],
        [20.80424850305562, -101.317782417662],
        [20.8039718702718, -101.3180941299268],
        [20.80362535798585, -101.3181298529317],
        [20.80356124964167, -101.3178967993306],
        [20.8035134911129, -101.317896799356],
        [20.80349154141035, -101.3177301412597],
        [20.80355735254284, -101.3174942322545],
        [20.80363845463535, -101.3172418871162],
        [20.80387789784647, -101.3169566451712],
        [20.80449310197798, -101.3164381201184],
        [20.80451386620924, -101.3173144171249]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    },
    {
      id: 'area-a3-249',
      name: 'Vista Pinos',
      image: '/Images/properties/monte-encinos-bosque-cazahuate.png',
      status: 'sold',
      size: '6,100 sqm',
      location: 'Guanajuato, Mexico',
      description: '',
      encumbrances: 'None',
      officialName: '249-A3',
      coordinates: [
        // Converted from KML (lon,lat) -> [lat, lon]
        [20.80501736724387, -101.3156954327486],
        [20.80502745865135, -101.3154963299546],
        [20.80566502661263, -101.3154471627536],
        [20.80575133742461, -101.3162516493298],
        [20.80540284817389, -101.3163653818739],
        [20.80496549196465, -101.3159094207514],
        [20.80501736724387, -101.3156954327486]
      ],
      color: '#C9A227',
      sold: true,
      features: [
        'SOLD'
      ]
    },
    {
      id: 'area-a4-249',
      name: 'Bosque Cazahuate',
      image: '/Images/properties/monte-encinos-bosque-cazahuate.png',
      status: 'sold',
      location: 'Guanajuato, Mexico',
      description: '',
      size: '3,700 sqm',
      encumbrances: 'None',
      officialName: '249-A4',
      coordinates: [
        // Converted from KML (lon,lat) -> [lat, lon]
        [20.80503114091487, -101.3154822980844],
        [20.80502889169724, -101.3152569611824],
        [20.80498357153236, -101.315043886292],
        [20.80559133249705, -101.3148098794654],
        [20.8056641486131, -101.3154289758784],
        [20.80503114091487, -101.3154822980844]
      ],
      color: '#C9A227',
      sold: true,
      features: [
        'SOLD'
      ]
    },
    {
      id: 'a1-249',
      name: 'Monte Encinos',
      image: '/Images/properties/monte-encinos-bosque-cazahuate.png',
      status: 'available',
      price: '3,000 MXN per sqm',
      size: '4,000 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Potential for commercial area development, with direct access to main boulevard.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: '249-A1',
      coordinates: [
        [20.80478664164971, -101.3162507843726],
        [20.80522439119102, -101.3167364970001],
        [20.80508616190493, -101.3171335301405],
        [20.80467185867014, -101.3170027187406],
        [20.80456862057381, -101.3164480698679],
        [20.80469620333465, -101.3163499615955],
        [20.80478664164971, -101.3162507843726]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Potential for commercial area development'
      ]
    },
    {
      id: 'area-vertical-2',
      name: 'Cerro Dátil',
      image: '/Images/properties/altos-laurel-cerro-datil.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '5,200 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zone 2 down',
      coordinates: [
        [20.80332208700704, -101.3178889410338],
        [20.80298013025774, -101.3178995121221],
        [20.80289148336193, -101.316583095792],
        [20.8040560659688, -101.3164619600737],
        [20.80385164705761, -101.3166021405979],
        [20.80361947415367, -101.3167985879118],
        [20.80339146776229, -101.3170540018728],
        [20.80330678160918, -101.3173652773931],
        [20.80332208700704, -101.3178889410338]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    },
    {
      id: 'a2-249',
      name: 'Rincón Ciprés',
      image: '/Images/properties/monte-encinos-bosque-cazahuate.png',
      status: 'available',
      price: '3,000 MXN per sqm',
      size: '2,600 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Potential for commercial area development, with direct access to main boulevard.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: '249-A2',
      coordinates: [
        [20.805223186126, -101.3167278392467],
        [20.80479677478719, -101.3162502991751],
        [20.80487599346115, -101.3161188146935],
        [20.80496171243514, -101.3159155671327],
        [20.80539895005396, -101.316372396011],
        [20.805223186126, -101.3167278392467]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Direct access to main boulevard'
      ]
    }
    ,
    {
      id: 'zona-vertical-3',
      name: 'Olivo Azul',
      image: '/Images/properties/olivo-azul-oasis-nogal.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '26,200 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zona 3 up left',
      coordinates: [
        [20.80489239578197, -101.3150611798109],
        [20.80467038888526, -101.3137648789008],
        [20.8057640057079, -101.313498401359],
        [20.806883039487, -101.3131423091608],
        [20.80708804723765, -101.3143886457255],
        [20.80554843308019, -101.3147923529594],
        [20.80489239578197, -101.3150611798109]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    }
    ,
    {
      id: 'area-vertical-5',
      name: 'Llanos Arrayán',
      image: '/Images/properties/llanos-arrayan-monte-huizache.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '5,800 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zona 3 down left',
      coordinates: [
        [20.80445656677989, -101.311520462367],
        [20.80496221011091, -101.3114451870271],
        [20.80526978875473, -101.3126162191762],
        [20.80526609213216, -101.3128497595715],
        [20.80565524656889, -101.3126211478613],
        [20.8061035700554, -101.3128269545731],
        [20.80619880889774, -101.3132753605532],
        [20.8047313259585, -101.3136055082534],
        [20.80481719120946, -101.3128127897752],
        [20.80474273765505, -101.3120855333485],
        [20.80464756247335, -101.3117613660597],
        [20.80445656677989, -101.311520462367]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    },
    {
      id: 'area-vertical-4',
      name: 'Mirador Álamo',
      image: '/Images/properties/olivo-azul-oasis-nogal.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '16,400 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zona 3 up right',
      coordinates: [
        [20.80466273121836, -101.3151970111523],
        [20.80459082277356, -101.3152507870248],
        [20.80431054894718, -101.3152916282299],
        [20.80419249869836, -101.3148152954734],
        [20.80415377421772, -101.3145086303599],
        [20.80412420215421, -101.3140437145987],
        [20.80412207163945, -101.313772910172],
        [20.80427158572407, -101.313764505594],
        [20.80446242121997, -101.31370874484],
        [20.80453813992789, -101.3144404170823],
        [20.80466273121836, -101.3151970111523]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    },
    {
      id: 'cluster-260',
      name: 'Senderos Sauce',
      image: '/Images/properties/olivo-azul-oasis-nogal.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '26,800 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Excellent topography with direct access to main boulevard.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: '260',
      coordinates: [
        [20.80306450276994, -101.3160179866608],
        [20.80310905857218, -101.3154585892592],
        [20.80313839800873, -101.3136818411895],
        [20.80362774821486, -101.3137332928869],
        [20.80411378408234, -101.3137681328387],
        [20.80409748672078, -101.3139972055555],
        [20.80412232395671, -101.314257353996],
        [20.80415975198498, -101.3146984626916],
        [20.80420915427987, -101.3150370027116],
        [20.80432156906594, -101.3154424091643],
        [20.80444779619858, -101.3160155490303],
        [20.80306450276994, -101.3160179866608]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Excellent topography with direct access to main boulevard.'
      ]
    },
    {
      id: 'area-vertical-6',
      name: 'Paraíso Madroño',
      image: '/Images/properties/llanos-arrayan-monte-huizache.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '5,600 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Prime land conveniently located near the highway with excellent access and visibility. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: 'Zona 3 down right',
      coordinates: [
        [20.80420712287865, -101.3117292234599],
        [20.80429486631269, -101.3119604879165],
        [20.80440875509645, -101.3120438574871],
        [20.80451607837834, -101.3126151091219],
        [20.80452056165169, -101.3134559016012],
        [20.80422328076123, -101.3134610864082],
        [20.80415217934499, -101.3129781150922],
        [20.80409013890447, -101.3128930966355],
        [20.80391981198305, -101.3115968069384],
        [20.80408058655471, -101.3115751723758],
        [20.80420712287865, -101.3117292234599]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Approved vertical commercial development with excellent highway access and visibility'
      ]
    },
    {
      id: 'cluster-261',
      name: 'Monte Huizache',
      image: '/Images/properties/llanos-arrayan-monte-huizache.png',
      status: 'available',
      price: '2,000 MXN per sqm',
      size: '8,200 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Dual access via Aldama neighborhood road and main boulevard.',
      encumbrances: 'None',
      utilities: 'Yes',
      approvedFor: 'Mixed-use development',
      officialName: '261',
      coordinates: [
        [20.80348826843242, -101.3135865763805],
        [20.80349457925787, -101.312479903923],
        [20.80401037458799, -101.3124634498251],
        [20.80408471862862, -101.3128916228477],
        [20.80414482509008, -101.3129622859973],
        [20.80422381988635, -101.3136459336568],
        [20.80348826843242, -101.3135865763805]
      ],
      additionalCoordinates: [
        [
          [20.80457306753382, -101.3136200910915],
          [20.80423185242888, -101.3136372408769],
          [20.80420875469235, -101.3134761806162],
          [20.80456368231356, -101.3134658662468],
          [20.80455991342066, -101.3135270302168],
          [20.80457306753382, -101.3136200910915]
        ]
      ],
      color: '#C9A227',
      features: [
        'Utility access: Yes',
        'Approved for: Mixed-use development',
        'Dual access via Aldama road and main boulevard'
      ]
    },
    {
      id: 'ml-1005',
      name: 'Cedro Real',
      image: '/Images/properties/cedro-real.png',
      status: 'available',
      price: '750 MXN per sqm',
      size: '124,500 sqm',
      location: 'Guanajuato, Mexico',
      description: 'Flat land with excellent topography and direct access to boulevard. It borders green area and offers beautiful views.',
      encumbrances: 'None',
      utilities: 'No',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: '1005',
      coordinates: [
        [20.8040112453937, -101.2979623033625],
        [20.80394935521471, -101.2981746234141],
        [20.80405798277286, -101.2983358599312],
        [20.80415540115711, -101.2986239954588],
        [20.80423479446172, -101.2990683082923],
        [20.80414698387844, -101.2994897292158],
        [20.8038780335288, -101.2998636984891],
        [20.79929116111224, -101.3004531580604],
        [20.7990703810845, -101.2983192814983],
        [20.80396658181629, -101.2976807460357],
        [20.8040112453937, -101.2979623033625]
      ],
      color: '#C9A227',
      features: [
        'Utility access: No',
        'Approved for: Commercial & Services, or Residential use',
        'Flat land with excellent topography and beautiful views'
      ]
    },
    {
      id: 'ml-3006',
      name: 'Parque Palmares',
      image: '/Images/properties/parque-palmares.png',
      status: 'available',
      price: '3,000 MXN per sqm',
      size: '39,400 sqm',
      location: 'Guanajuato, Mexico',
      description: 'This land offers unparalleled privacy, situated next to a scenic ravine with large frontage along the boulevard. It features the best views in the entire development, surrounded by green areas and panoramic vistas.',
      encumbrances: 'None',
      utilities: 'No',
      approvedFor: 'Commercial & Services, or Residential use',
      officialName: '3006',
      coordinates: [
        [20.80111060553743, -101.3024077762708],
        [20.80196749286162, -101.3015906320046],
        [20.80264824580811, -101.3011963577352],
        [20.80356591168451, -101.3006238900364],
        [20.80413447797691, -101.3000456396881],
        [20.80449263970589, -101.299407328113],
        [20.80464709721975, -101.299568968304],
        [20.80424945355822, -101.3005100289936],
        [20.80420431496542, -101.301040393311],
        [20.80415562511413, -101.3015695795404],
        [20.80394630514218, -101.3018669803259],
        [20.8036636265719, -101.302037730966],
        [20.80290630174255, -101.302225241358],
        [20.80254416504652, -101.3022194748271],
        [20.80205797146162, -101.3022716957766],
        [20.80148529494341, -101.3025234308278],
        [20.80128376645641, -101.302647498948],
        [20.80111060553743, -101.3024077762708]
      ],
      color: '#C9A227',
      features: [
        'Utility access: No',
        'Approved for: Commercial & Services, or Residential use',
        'Best views in the entire development with unparalleled privacy.'
      ]
    }
  ];

  const handleParcelSelect = (parcel) => {
    setSelectedParcel(parcel);
    // Switch to map tab when a parcel is selected from the properties list
    setActiveTab('map');
    window.history.pushState(null, '', '#map');
    window.scrollTo(0, 0);
  };

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    window.history.pushState(null, '', `#${tabId}`);
    window.scrollTo(0, 0);
  }, []);

  const renderTabContent = () => {
    // Helper to localize unit strings based on current language
    const localizeUnits = (str) => {
      if (!str || typeof str !== 'string') return str;
      if (lang === 'es') {
        return str
          .replace(/per sqm/g, 'por m²')
          .replace(/sqm/g, 'm²')
          .replace(/Mexico/g, 'México')
          .replace(/Utility access: Yes/g, 'Acceso a servicios: Sí')
          .replace(/Utility access: No/g, 'Acceso a servicios: No')
          .replace(/Approved for:/g, 'Uso de suelo autorizado:')
          .replace(/Mixed-use development/g, 'Desarrollo de uso mixto')
          .replace(/Commercial & Services, or Residential use/g, 'Comercial y de Servicios, o Habitacional')
          // Third bullet point translations

          .replace(/Elevated site with beautiful views/g, 'Terreno elevado con hermosas vistas')
          .replace(/Panoramic sunset views/g, 'Hermosas vistas panorámicas')
          .replace(/Privileged position close to main access, adjacent to green areas and lake/g, 'Ubicación privilegiada cercana al acceso principal, colindante con áreas verdes y lago')
          .replace(/Unparalleled privacy with scenic views/g, 'Privacidad inigualable con vistas escénicas')
          .replace(/Excellent topography with boulevard frontage/g, 'Excelente topografía con frente al bulevar')
          .replace(/Beautiful panoramic views/g, 'Hermosas vistas panorámicas')
          .replace(/Panoramic views with scenic rocky outcrops/g, 'Hermosas vistas panorámicas')
          .replace(/Dual access via Aldama road and main boulevard/g, 'Doble acceso por camino de Aldama y bulevar principal')
          .replace(/Prime commercial zone with highway visibility/g, 'Zona comercial estratégica con visibilidad a carretera')
          .replace(/Approved vertical commercial development with excellent highway access and visibility/g, 'Desarrollo comercial vertical autorizado con excelente acceso y visibilidad a carretera')
          .replace(/Potential for commercial area development/g, 'Potencial para desarrollo de área comercial')
          .replace(/Direct access to main boulevard/g, 'Acceso directo al bulevar principal')
          .replace(/Excellent topography with direct access to main boulevard\./g, 'Excelente topografía con acceso directo al bulevar principal')
          .replace(/Flat land with excellent topography and beautiful views/g, 'Terreno plano con excelente topografía y hermosas vistas')
          .replace(/Best views in the entire development with unparalleled privacy\./g, 'Mejores vistas del desarrollo con privacidad incomparable.')
          .replace(/SOLD/g, 'VENDIDO')
          // Description translations
          .replace(/Adjacent to lake and green areas, with privileged position close to main access while preserving privacy\./g, 'Colindante con lago y áreas verdes, con ubicación privilegiada cercana al acceso principal manteniendo privacidad y exclusividad.')
          .replace(/Elevated site with beautiful views and exceptional privacy, adjacent to the lake and large green area\./g, 'Terreno elevado con vistas espectaculares y privacidad excepcional, colindante con el lago y amplia zona verde.')
          .replace(/Vast green surroundings with panoramic sunset views\./g, 'Entorno natural con amplias áreas verdes y vistas panorámicas al atardecer.')
          .replace(/Flat land with excellent topography and gentle contours, with extensive frontage along the boulevard and strong potential for commercial use, adjacent to green area\./g, 'Terreno plano con excelente topografía y curvas de nivel, colindante con área verde, con amplio frente al bulevar y gran potencial comercial.')
          .replace(/Adjacent to green area with beautiful panoramic views\./g, 'Colindante con área verde con hermosas vistas panorámicas.')
          .replace(/Private cluster with panoramic views and adjoining green areas\. Offers incredible vistas and scenic rocky outcrops\./g, 'Clúster privado con vistas panorámicas, amplias áreas verdes adyacentes y peñasco.')
          .replace(/Dual access via Aldama neighborhood road and main boulevard\. Prepared for vertical construction with a solid hydraulic base of tepetate soil\./g, 'Doble acceso por vialidad hacia Aldama y por el bulevar principal del desarollo. Terreno listo para construcción vertical con base de tepetate.')
          .replace(/Dual access via Aldama neighborhood road and main boulevard\./g, 'Doble acceso por vialidad hacia Aldama y por el bulevar principal del desarrollo.')
          .replace(/Prime commercial zone potential with high-impact visibility, directly adjacent to the highway\./g, 'Zona con alto potencial comercial, visibilidad estratégica e inmediata colindancia con la carretera.')
          .replace(/Prime commercial zone potential with high-impact visibility, directly adjacent to highway\./g, 'Excelente visibilidad sobre la autopista principal, ideal para proyectos comerciales de alto impacto.')
          .replace(/Prime land conveniently located near the highway with excellent access and visibility\. One of the few parcels available with approved vertical commercial development, offering high potential for commercial or residential projects\./g, 'Ubicación privilegiada cercana a la autopista, con fácil acceso y excelente exposición. Uno de los pocos terrenos con aprobación para desarrollo comercial vertical, ideal para proyectos residenciales o de servicios.')
          .replace(/Potential for commercial area development, with direct access to main boulevard\./g, 'Potencial para desarrollo de área comercial, con acceso directo al bulevar principal.')
          .replace(/Excellent topography with direct access to main boulevard\./g, 'Excelente topografía con acceso directo al bulevar principal.')
          .replace(/Flat land with excellent topography and direct access to boulevard\. It borders green area and offers beautiful views\./g, 'Terreno plano con excelente topografía y acceso directo al bulevar. Colinda con área verde y ofrece bellas vistas.')
          .replace(/This land offers unparalleled privacy, situated next to a scenic ravine with large frontage along the boulevard\. It features the best views in the entire development, surrounded by green areas and panoramic vistas\./g, 'Terreno con privacidad excepcional, ubicado junto a una cañada scénica y con amplio frente al bulevar. Posee las mejores vistas en venta, rodeado de áreas verdes y vistas panorámicas.')
          .replace(/Residential area/g, 'Zona residencial');

      }
      return str;
    };

    const localizedParcels = landParcels.map(parcel => ({
      ...parcel,
      price: localizeUnits(parcel.price),
      size: localizeUnits(parcel.size),
      location: localizeUnits(parcel.location),
      description: localizeUnits(parcel.description),
      features: (parcel.features || []).map(f => localizeUnits(f)),
    }));

    switch (activeTab) {
      case 'main':
        return <MainTab />;
      case 'who':
        return <AboutTab />;
      case 'spaces':
        return <PropertiesTab 
          parcels={localizedParcels.map(parcel => ({
            ...parcel,
            statusColor: parcel.status === 'available' ? '#8B6914' : '#7A7D82',
            priceDisplay: parcel.price ? `${parcel.price}` : 'n/a'
          }))}
          onParcelSelect={handleParcelSelect}
          selectedParcel={selectedParcel}
        />;
      case 'map':
        return (
          <div className="tab-content map-tab">
            <div className="container">
              <div className="map-container">
                <InteractiveMap 
                  parcels={localizedParcels}
                  onParcelSelect={setSelectedParcel}
                  highlightedParcel={selectedParcel}
                />
              </div>
              <div className="sidebar">
                <Sidebar 
                  selectedParcel={selectedParcel ? {
                    ...selectedParcel,
                    price: localizeUnits(selectedParcel.price),
                    size: localizeUnits(selectedParcel.size),
                    location: localizeUnits(selectedParcel.location),
                    description: localizeUnits(selectedParcel.description),
                    features: (selectedParcel.features || []).map(f => localizeUnits(f)),
                  } : null}
                  onContactClick={() => handleTabChange('contact')}
                />
              </div>
            </div>
          </div>
        );
      case 'contact':
        return <ContactTab />;
      case 'admin':
        return role === 'owner' ? <AdminTab /> : null;
      default:
        return <MainTab />;
    }
  };

  return (
    <I18nProvider lang={lang}>
      <div className="App">
        <Header
          activeTab={activeTab}
          onTabChange={handleTabChange}
          lang={lang}
          onToggleLang={toggleLang}
          role={role}
          onLogout={() => {
            sessionStorage.removeItem('auth_token');
            sessionStorage.removeItem('auth_role');
            window.location.reload();
          }}
        />
        {renderTabContent()}
      </div>
    </I18nProvider>
  );
};

export default App;
